import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Typography,
  Button,
  TableSortLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Location } from '../../types/location.types';

interface LocationListProps {
  locations: Location[];
  onEdit: (location: Location) => void;
  onView: (location: Location) => void;
  onDelete: (location: Location) => void;
  onAddNew: () => void;
  onSearch: (query: string) => void;
  page: number;
  rowsPerPage: number;
  totalRows: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  isLoading?: boolean;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

const LocationList: React.FC<LocationListProps> = ({
  locations,
  onEdit,
  onView,
  onDelete,
  onAddNew,
  onSearch,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  isLoading = false,
  searchQuery = '',
  sortBy = 'name',
  sortOrder = 'asc',
  onSort,
}) => {
  const handleSort = (field: string) => {
    if (onSort) {
      onSort(field);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const getLocationTypeLabel = (type: string) => {
    switch (type) {
      case 'BUILDING':
        return 'Building';
      case 'FLOOR':
        return 'Floor';
      case 'ROOM':
        return 'Room';
      case 'WARD':
        return 'Ward';
      case 'DEPARTMENT':
        return 'Department';
      default:
        return type;
    }
  };

  const renderSortableHeader = (field: string, label: string) => (
    <TableCell>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        
      </Box>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="locations table">
          <TableHead>
            <TableRow>
              {renderSortableHeader('code', 'Code')}
              {renderSortableHeader('name', 'Name')}
              {renderSortableHeader('locationType', 'Type')}
              <TableCell>Facility</TableCell>
              <TableCell>Parent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading locations...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : locations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">
                    {searchQuery ? 'No locations found matching your search.' : 'No locations found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              locations.map((location) => (
                <TableRow hover key={location.id}>
                  <TableCell>{location.code}</TableCell>
                  <TableCell>{location.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={getLocationTypeLabel(location.locationType)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{location.facility?.name || '-'}</TableCell>
                  <TableCell>{location.parent?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={location.isActive ? 'Active' : 'Inactive'}
                      color={location.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => onView(location)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(location)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(location)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default LocationList;
